import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'monetize-telegram-channel-with-stars',
  title: 'How to Monetize a Telegram Channel with Stars',
  metaTitle: 'Monetize a Telegram Channel with Stars',
  metaDescription:
    'A practical playbook to charge for a Telegram channel with Stars: free vs paid, what to pin, price bands that convert, and how access is granted after payment.',
  keywords: [
    'monetize telegram channel',
    'paid telegram channel',
    'telegram channel subscription',
    'charge for telegram channel',
    'telegram stars channel',
    'telegram paid group stars',
  ],
  category: 'Business',
  datePublished: '2026-10-24',
  dateModified: '2026-10-24',
  readingTimeMin: 8,
  excerpt:
    'A public channel can stay free and still make money, or you can put the archive and the daily posts behind a Stars payment. This playbook covers which model to pick, what to charge, and how a buyer actually gets in.',
  keyTakeaways: [
    'Keep a public channel for reach, and sell a private channel (or a private tier) with a Stars subscription.',
    'Pin one payment link, restate the offer in the description, and use a short redirect so you can change the price without editing every post.',
    'Monthly access should be an automatic 30-day Stars subscription. A launch week or a workshop fits a manual period of 7 to 90 days.',
    'Access is removed when the period ends, so the description on the payment sheet must say how long membership lasts.',
  ],
  body: [
    h2('Pick a model before you pick a price'),
    p(
      'Most channels that earn with Stars do not paywall everything on day one. They keep a public surface so new people can judge the work, then sell a clearer offer next to it. Three models cover almost every channel:',
    ),
    table(
      ['Model', 'What stays public', 'What is paid'],
      [
        ['Tip jar', 'Every post', 'An optional Stars link under strong posts'],
        ['Public plus private', 'Teasers, news, one free thread a week', 'A private channel with the full archive and daily posts'],
        ['Paid only', 'A short description and the payment link', 'The whole channel'],
      ],
    ),
    p(
      'The tip jar is the easiest test and the weakest business. A public-plus-private channel is the default that works: the free side proves you post, the paid side has a reason to exist. Fully paid channels work when the audience already knows you from YouTube, X, a newsletter or another Telegram channel.',
    ),

    h2('What to sell inside the paid channel'),
    ul([
      '**The posts themselves**, if they are timely: deals, research, signals, local news.',
      '**A filtered archive**: the public channel gets the headline, the private one gets the file, the template or the numbers.',
      '**A cadence you can keep**. Daily is a promise. If you can only write twice a week, say that in the payment description.',
      '**A boundary**. Paid members should know what you will not do (no DMs, no 1:1 calls) so refund requests stay rare.',
    ]),
    callout(
      'Channel access belongs on a subscription invoice. A one-off content sale can deliver a file or a link, but not membership of a private channel. The setup is in [Telegram Stars subscriptions](/blog/telegram-stars-subscriptions).',
    ),

    h2('Price bands that match how people buy Stars'),
    p(
      'Buyers top up in packs. A price that sits just above a common pack forces a second purchase and drops conversion. These bands are a starting point, not a rule:',
    ),
    table(
      ['Offer', 'Stars', 'Rough buyer cost'],
      [
        ['Tip / single essay', '50–100', 'About $1–2'],
        ['Monthly membership', '250–500', 'About $4–10'],
        ['Serious research or signals', '1,000', 'About $15–20'],
        ['High-trust niche, small audience', '2,500', 'About $40'],
      ],
    ),
    p(
      'In-app Stars cost roughly $0.015 to $0.025 each, and sellers historically receive about $0.013 per Star before a platform fee. Check the live rate in [Telegram Stars price in USD and TON](/blog/telegram-stars-price-usd-ton) before you lock a price for a quarter.',
    ),

    h2('The post that does the selling'),
    ol([
      'Write a pinned message with three lines: who it is for, what they get each week, and the price in Stars.',
      'Put a single link in that message. Use a redirect (`telegram-stars.io/r/your-channel`) so a price change does not require a new pin.',
      'Repeat the link in the channel description. People read the description more often than the 40th post.',
      'Once a week, publish a free post that ends with one sentence and the same link. Do not add a second offer.',
    ]),

    h2('How the buyer gets in'),
    p(
      'Create a **Subscription** invoice, point delivery at the private channel ID (`-100…`), and add the bot as an admin who can invite and remove members. Your own account stays an admin. After payment, the buyer is treated as active until the period ends. If they do not renew, access is removed.',
    ),
    ul([
      'Automatic renewal: 30 days, up to 10,000 Stars.',
      'Manual period: 1 to 365 days, useful for a launch cohort or a 7-day trial paid up front.',
      'Say the rule on the payment sheet. “Renews every 30 days” is clearer than “premium”.',
    ]),

    h2('Numbers worth watching'),
    ul([
      '**Clicks on the redirect** versus payments. A big gap means the sheet price or the description is the problem, not the channel topic.',
      '**Renewals**, not the first week of sales. A channel that converts and then expires is a content problem.',
      '**Refunds and removals**. If people pay and leave within days, the pinned promise is ahead of what you post.',
    ]),
    p('Payouts still wait 21 days per payment, then go to TON from 100 Stars, with a 3% fee on Telegram Stars Payment Platform. Details: [withdraw Telegram Stars to TON](/blog/withdraw-telegram-stars-to-ton).'),
  ],
  faq: [
    {
      q: 'Can I charge for a public channel?',
      a: 'A public channel cannot be members-only. Keep it public for reach, and sell a private channel that the bot manages after a Stars payment.',
    },
    {
      q: 'Do I need my own bot?',
      a: 'No. A subscription invoice on Telegram Stars Payment Platform uses the platform bot. You add that bot as an administrator of the private channel.',
    },
    {
      q: 'Should the first month be cheaper?',
      a: 'Use a manual 30-day invoice at the intro price, then point the redirect to the regular automatic subscription. One link in the pin, two prices over time.',
    },
    {
      q: 'What if I already have a paid bot?',
      a: 'You can keep it. A payment link is still useful for the pinned post, because people who have never messaged the bot can pay from the channel.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-subscriptions',
    'sell-digital-products-telegram-stars',
    'telegram-stars-payment-links',
    'telegram-stars-price-usd-ton',
  ],
};

export default article;
